import {
  Badge,
  Box,
  Button,
  Card,
  convertPenceToPounds,
  Flex,
  Link as StyledLink,
  Typography,
} from '@fe-toolkit/web-components/ssr'

import { DetailsListSeparator } from '@giffgaff/broadband-common'

import { customEvent } from '@fe-toolkit/eventing'

import { ComponentProps, PropsWithChildren } from 'react'

import { formatDate, shortMonthInformal, shortMonthInformalFullYear } from '@/utils/formatDate/formatDate'

import { dayjs } from '@/lib/dates'

import AdaptiveAlert from '@/components/molecules/AdaptiveAlert'

import { CARD_BRAND_MAP } from '@/constants'

import { ProfileSection } from '../ProfileSection'

import type { ECommerceOrderScheduleEntry, OrchestratedPayment, PaymentCard, Plan, ProfileOrder } from '@/api'

export type PaymentsSectionProps = {
  ecommerceOrderSchedule: ECommerceOrderScheduleEntry | undefined
  cardDetails: PaymentCard | undefined
  order: Pick<ProfileOrder, 'requestedOrderCompletionDate'> | undefined
  plan: Pick<Plan, 'monthlyPrice'> | undefined
  orchestratedPayments: OrchestratedPayment[] | undefined
  compareAgainstDate?: string
}

export function PaymentsSection({
  ecommerceOrderSchedule,
  cardDetails,
  order,
  plan,
  orchestratedPayments,
}: Readonly<PaymentsSectionProps>) {
  const paymentStatus = getPaymentStatus(orchestratedPayments)

  const badge = getStatusBadgeProps(paymentStatus)
  const primaryPayment = getPrimaryPaymentInfo(orchestratedPayments, order, plan)
  const nextPayment = getNextPaymentInfo(paymentStatus, ecommerceOrderSchedule)
  const paymentFailures = getPaymentFailures(orchestratedPayments)
  const paymentDelay = getPaymentDelay(paymentStatus, ecommerceOrderSchedule, order)
  const paymentMethod = getPaymentMethod(cardDetails)

  return (
    <ProfileSection>
      <Typography as="h2" color="black" marginBottom="small" variant="loudBold">
        My payments
      </Typography>
      <Card
        backgroundColor="white"
        borderRadius="medium"
        justifyContent="space-between"
        padding="small"
        sx={{ flexGrow: 1 }}
      >
        {badge && (
          <Box data-testid="payment-status-badge" marginBottom="small">
            <Badge backgroundColor={badge.backgroundColor} color={badge.color} variant="murmurBold">
              {badge.children}
            </Badge>
          </Box>
        )}

        <Box as="ul">
          {primaryPayment && (
            <Box as="li">
              <PaymentSectionItem data-testid="primary-payment" label={primaryPayment.label}>
                <Flex justifyContent="space-between">
                  <Typography as="p" variant="loudBold">
                    {primaryPayment.date}
                  </Typography>
                  <Typography as="p" variant="loudBold">
                    {primaryPayment.amount}
                  </Typography>
                </Flex>

                {paymentFailures && (
                  <AdaptiveAlert
                    isMinor
                    borderRadius="small"
                    data-testid="payment-failure"
                    marginTop="small"
                    role="status"
                    variant="error"
                  >
                    <Typography as="p" variant="speakUp">
                      {paymentFailures.label}
                    </Typography>

                    {paymentFailures.failedPaymentsDueDates.length > 0 && (
                      <Box marginBottom="small">
                        {paymentFailures.failedPaymentsDueDates.map((date, index) => (
                          <Typography key={index} as="p" variant="speak">
                            {date}
                          </Typography>
                        ))}
                      </Box>
                    )}

                    {paymentFailures.copy.map((line, index) => (
                      <Typography key={index} as="p" marginBottom="small" variant="speak">
                        {line}
                      </Typography>
                    ))}

                    <Box>
                      <Button isSmall href="/profile/payment-details" sx={{ marginTop: 0 }} variant="standard">
                        {paymentFailures.ctaLabel}
                      </Button>
                    </Box>
                  </AdaptiveAlert>
                )}

                {paymentDelay && (
                  <AdaptiveAlert
                    isMinor
                    borderRadius="small"
                    data-testid="payment-delay"
                    marginTop="small"
                    role="status"
                    variant="info"
                  >
                    <Typography as="p" variant="speakUp">
                      {paymentDelay.label}
                    </Typography>

                    <Typography as="p" variant="speak">
                      {paymentDelay.copy}
                    </Typography>
                  </AdaptiveAlert>
                )}
              </PaymentSectionItem>
              <DetailsListSeparator colour="alto" />
            </Box>
          )}

          {nextPayment && (
            <Box as="li">
              <PaymentSectionItem data-testid="next-payment" label={nextPayment.label}>
                {nextPayment.date}
              </PaymentSectionItem>
              <DetailsListSeparator colour="alto" />
            </Box>
          )}

          {paymentMethod && (
            <Box as="li">
              <PaymentSectionItem data-testid="payment-method" label={paymentMethod.label}>
                <Typography as="p" variant="speak">
                  {paymentMethod.card}
                </Typography>
                <Typography as="p" variant="speak">
                  {paymentMethod.expiry}
                </Typography>
              </PaymentSectionItem>
              <DetailsListSeparator colour="alto" />
            </Box>
          )}
        </Box>

        <StyledLink
          chevron
          fullWidth
          arrow="none"
          href="/orders/history/others"
          marginBottom="small"
          marginTop="small"
          onClick={() => {
            customEvent('gg_click', { contextType: 'bb_dashboard_order_payment_click' })
          }}
        >
          View payment history
        </StyledLink>

        <DetailsListSeparator colour="alto" />

        <StyledLink
          chevron
          fullWidth
          arrow="none"
          href="/profile/payment-details"
          marginBottom="small"
          marginTop="small"
        >
          Edit payment method
        </StyledLink>
      </Card>
    </ProfileSection>
  )
}

function PaymentSectionItem({
  label,
  children,
  'data-testid': dataTestId,
}: PropsWithChildren<{ label: string; 'data-testid'?: string }>) {
  const labelDataTestId = dataTestId ? { 'data-testid': `${dataTestId}-label` } : {}
  const valueDataTestId = dataTestId ? { 'data-testid': `${dataTestId}-value` } : {}

  return (
    <Box data-testid={dataTestId}>
      <Typography as="p" {...labelDataTestId} variant="whisper">
        {label}
      </Typography>
      {typeof children === 'string' ? (
        <Typography as="p" {...valueDataTestId} variant="speak">
          {children}
        </Typography>
      ) : (
        children
      )}
    </Box>
  )
}

function getPaymentStatus(orchestratedPayments?: OrchestratedPayment[]) {
  const lastPayment = orchestratedPayments && orchestratedPayments[0]
  const lastPaymentStatus = lastPayment?.status

  if (!lastPaymentStatus) {
    return 'Pending'
  }

  switch (lastPaymentStatus) {
    case 'SUCCEEDED':
      return 'Paid'
    case 'RESCHEDULED':
    case 'EXHAUSTED':
      return 'Failed'
  }

  return 'Unknown'
}

function getStatusBadgeProps(
  paymentStatus: ReturnType<typeof getPaymentStatus>,
): ComponentProps<typeof Badge> | undefined {
  switch (paymentStatus) {
    case 'Paid':
      return { children: paymentStatus, backgroundColor: 'green', color: 'black' }
    case 'Failed':
      return { children: paymentStatus, backgroundColor: 'red', color: 'white' }
    case 'Pending':
      return { children: paymentStatus, backgroundColor: 'alto', color: 'black' }
  }

  return undefined
}

function getPrimaryPaymentInfo(
  orchestratedPayments: OrchestratedPayment[] | undefined,
  order: Pick<ProfileOrder, 'requestedOrderCompletionDate'> | undefined,
  plan: Pick<Plan, 'monthlyPrice'> | undefined = undefined,
) {
  if (!orchestratedPayments || orchestratedPayments.length === 0) {
    if (!order && !plan) {
      return undefined
    }

    return {
      date: formatDate(order?.requestedOrderCompletionDate, {
        options: shortMonthInformal,
        defaultValue: '-',
      }),
      amount: plan?.monthlyPrice ? `£${plan.monthlyPrice}` : '-',
      label: 'First payment due on',
    } as const
  }

  const lastPayment = orchestratedPayments[0]
  const lastPaymentStatus = lastPayment?.status

  const rescheduledPaymentsTotal = orchestratedPayments
    .filter(payment => payment.status === 'RESCHEDULED')
    .reduce((acc, payment) => acc + payment.totals.amountInPenceIncludingTax, 0)

  switch (lastPaymentStatus) {
    case 'SUCCEEDED':
      return {
        date: formatDate(lastPayment.updated, { options: shortMonthInformal, defaultValue: '-' }),
        amount: convertPenceToPounds(lastPayment.totals.amountInPenceIncludingTax),
        label: 'Last payment',
      } as const
    case 'RESCHEDULED':
      return {
        date: formatDate(lastPayment.created, { options: shortMonthInformal, defaultValue: '-' }),
        amount: convertPenceToPounds(rescheduledPaymentsTotal),
        label: 'Last payment due',
      } as const
    case 'EXHAUSTED':
      return {
        date: formatDate(lastPayment.created, { options: shortMonthInformal, defaultValue: '-' }),
        amount: convertPenceToPounds(lastPayment.totals.amountInPenceIncludingTax),
        label: 'Last payment due',
      } as const
  }

  return undefined
}

function getECommerceNextPaymentDate(ecommerceOrderSchedule?: ECommerceOrderScheduleEntry) {
  if (!ecommerceOrderSchedule) return undefined

  const {
    recurrenceSpec: { validUntil: validUntilString },
    nextOccurrence: nextOccurrenceString,
  } = ecommerceOrderSchedule

  // this valid check can be removed after BBND-3948
  const validUntil = validUntilString && new Date(validUntilString)
  const nextOccurrence = nextOccurrenceString && new Date(nextOccurrenceString)

  return nextOccurrence == null || (validUntil != null && +validUntil < +nextOccurrence)
    ? undefined
    : nextOccurrenceString
}

function getNextPaymentInfo(
  paymentStatus: ReturnType<typeof getPaymentStatus>,
  ecommerceOrderSchedule: ECommerceOrderScheduleEntry | undefined,
) {
  const nextDate = getECommerceNextPaymentDate(ecommerceOrderSchedule)

  if (!nextDate || paymentStatus === 'Pending') return undefined

  return {
    date: formatDate(nextDate, {
      options: shortMonthInformalFullYear,
    }),
    label: 'Next payment due',
  }
}

function getPaymentMethod(cardDetails?: PaymentCard) {
  if (!cardDetails) return undefined

  const cardExpiryDate = dayjs(cardDetails.fourDigitExpiryDate, 'MMYY').endOf('month').toISOString()
  const cardBrand = CARD_BRAND_MAP[cardDetails.cardType] ?? 'Card'

  return {
    label: 'Payment method',
    card: `${cardBrand} •••• ${cardDetails.lastFourDigits}`,
    expiry: `Expires: ${formatDate(cardExpiryDate, {
      options: {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
      },
    })}`,
  }
}

function getPaymentFailures(orchestratedPayments: OrchestratedPayment[] | undefined) {
  if (!orchestratedPayments) return undefined

  // payments are already sorted by most recent first
  const failedPayments = orchestratedPayments.filter(payment => payment.status !== 'SUCCEEDED')

  if (failedPayments.length === 0) {
    return undefined
  }

  const failedPaymentsWithReschedule = failedPayments.filter(payment => payment.nextRetry !== undefined)

  const failedPaymentRescheduledTotal = failedPaymentsWithReschedule.reduce(
    (acc, payment) => acc + payment.totals.amountInPenceIncludingTax,
    0,
  )
  const paymentAtInterval =
    failedPaymentsWithReschedule.length > 1
      ? `payments totalling ${convertPenceToPounds(failedPaymentRescheduledTotal)}`
      : 'payment again'

  // we only want to show the retry date if there is a retry scheduled on the most recent failed payment
  // all payment retries should have the same date, so should be safe to grab the first one
  const retryDate =
    failedPayments[0] === failedPaymentsWithReschedule[0]
      ? formatDate(failedPaymentsWithReschedule[0].nextRetry, {
          options: shortMonthInformal,
        })
      : undefined

  const failedPaymentsDueDates = failedPayments.map(
    payment =>
      `${formatDate(payment.created, { options: shortMonthInformalFullYear })}: ${convertPenceToPounds(payment.totals.amountInPenceIncludingTax)}`,
  )

  const line1 = `We'll try to take ${paymentAtInterval} on ${retryDate}.`
  const line2 = 'Check your card details are up to date.'
  const copy = retryDate ? [line1, line2] : [line2]

  return {
    label: failedPaymentsWithReschedule.length > 1 ? 'Missed payments' : 'Missed payment',
    copy,
    ctaLabel: 'Review card details',
    failedPaymentsDueDates: failedPaymentsDueDates.length > 1 ? failedPaymentsDueDates : [],
  }
}

function getPaymentDelay(
  paymentStatus: ReturnType<typeof getPaymentStatus>,
  ecommerceOrderSchedule: ECommerceOrderScheduleEntry | undefined,
  order: Pick<ProfileOrder, 'requestedOrderCompletionDate'> | undefined,
) {
  if (paymentStatus !== 'Pending') return undefined

  const firstPayment = getECommerceNextPaymentDate(ecommerceOrderSchedule) || order?.requestedOrderCompletionDate

  if (firstPayment && dayjs(firstPayment).endOf('day').isBefore(dayjs(Date.now()).endOf('day'))) {
    return {
      label: 'Your first payment has been delayed',
      copy: 'We’ll take payment as soon as your service goes live.',
    }
  }

  return undefined
}
